/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType, createObservableDataAction, IAction, IActionContext, IActionInput,
    ICommerceApiSettings, ICreateActionContext, isEmptyOrNullObject
} from '@msdyn365-commerce/core';
import { getCheckoutState, ICheckoutState } from '@msdyn365-commerce/global-state';
import { CardPaymentAcceptPoint, CartsDataActions } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, buildCacheKey } from '@msdyn365-commerce-modules/retail-actions';
import { get } from 'lodash';

/**
 * Get card payment accept point input.
 */
export interface IGetCardPaymentAcceptPointInput {
    shouldShowBillingAddress?: boolean;
    paymentTenderType?: string;
    apiSettings: ICommerceApiSettings;
    shouldEnableSinglePaymentAuthorizationCheckout?: boolean;
    amountDue?: number;
}

/**
 * GetCardPaymentAcceptPointInput - Input for create GetCardPaymentAcceptPoint.
 */
export class GetCardPaymentAcceptPointInput implements IActionInput {
    public shouldShowBillingAddress?: boolean;

    public paymentTenderType?: string;

    public shouldEnableSinglePaymentAuthorizationCheckout?: boolean;

    public amountDue?: number;

    private readonly apiSettings: ICommerceApiSettings;

    // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility -- Existing code.
    constructor(input: IGetCardPaymentAcceptPointInput) {
        this.shouldShowBillingAddress = input.shouldShowBillingAddress;
        this.paymentTenderType = input.paymentTenderType;
        this.apiSettings = input.apiSettings;
        this.shouldEnableSinglePaymentAuthorizationCheckout = input.shouldEnableSinglePaymentAuthorizationCheckout;
        this.amountDue = input.amountDue;
    }

    /**
     * Get the cache key.
     * @returns Cache key.
     */
    // eslint-disable-next-line sonarjs/no-nested-template-literals -- Existing code.
    public getCacheKey = (): string => buildCacheKey(`CardPaymentAcceptPoint${this.paymentTenderType ? `-${this.paymentTenderType}` : ''}`, this.apiSettings);

    /**
     * Get cache object type.
     * @returns Card payment accept point.
     */
    public getCacheObjectType = (): string => 'CardPaymentAcceptPoint';

    /**
     * Get data cache type.
     * @returns The cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Create checkout express input.
 * @param inputData - Get card payment accept point.
 * @returns CardPaymentAcceptInput.
 */
export const createCheckoutPaymentInstrumentInput = (inputData: ICreateActionContext<IGetCardPaymentAcceptPointInput>): GetCardPaymentAcceptPointInput => {
    const { shouldShowBillingAddress, paymentTenderType, shouldEnableSinglePaymentAuthorizationCheckout, amountDue } = inputData.config ?? {};
    const getCardPaymentAcceptPointInput = {
        shouldShowBillingAddress,
        paymentTenderType,
        apiSettings: inputData.requestContext.apiSettings,
        shouldEnableSinglePaymentAuthorizationCheckout,
        amountDue
    };

    return new GetCardPaymentAcceptPointInput(getCardPaymentAcceptPointInput);
};

/**
 * Calls the Retail API and returns a cart object based on the passed GetCartInput.
 * @param input - The get card payment accept point.
 * @param ctx - The action context.
 * @returns CardPaymentAcceptPoint.
 */
export async function getCardPaymentAcceptPointAction(
    input: GetCardPaymentAcceptPointInput,
    // eslint-disable-next-line unicorn/prevent-abbreviations, @typescript-eslint/naming-convention -- Public name.
    ctx: IActionContext
): Promise<CardPaymentAcceptPoint> {
    // If no cart ID is provided in input, we need to create a cart object
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Double check.
    if (!input) {
        ctx.telemetry.exception(new Error('[getCardPaymentAcceptPointAction] No valid Input was provided, failing'));
        throw new Error('[getCardPaymentAcceptPointAction] No valid Input was provided, failing');
    }

    const { shouldShowBillingAddress, paymentTenderType, amountDue = 0 } = input;
    const checkoutState: ICheckoutState = await getCheckoutState(ctx).catch((error: Error) => {
        ctx.telemetry.exception(error);
        throw error;
    });

    if (
        isEmptyOrNullObject(checkoutState) ||
        isEmptyOrNullObject(checkoutState.checkoutCart) ||
        isEmptyOrNullObject(checkoutState.checkoutCart.cart)
    ) {
        ctx.telemetry.exception(new Error('[getCardPaymentAcceptPointAction] Unable to get cart'));
        throw new Error('[getCardPaymentAcceptPointAction] Unable to get cart');
    }

    if (!checkoutState.checkoutCart.cart.CartLines || !ArrayExtensions.hasElements(checkoutState.checkoutCart.cart.CartLines)) {
        ctx.telemetry.exception(new Error('[getCardPaymentAcceptPointAction] Cart is empty'));
        throw new Error('[getCardPaymentAcceptPointAction] Cart is empty');
    }

    const requestUrl =
        typeof ctx.requestContext.url.requestUrl === 'string' ? new URL(ctx.requestContext.url.requestUrl) : ctx.requestContext.url.requestUrl;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Existing code.
    const origin = get(window, 'location.origin') || requestUrl.origin;

    const cardPaymentAcceptSettings = {
        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CardPaymentAcceptSettings',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Any type.
        HostPageOrigin: origin,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-assignment -- Any type.
        AdaptorPath: `${origin}/Connectors/` || origin,
        CardPaymentEnabled: false,
        CardTokenizationEnabled: true,
        ...(!shouldShowBillingAddress && { HideBillingAddress: true }),
        ...(paymentTenderType && { TenderTypeId: paymentTenderType }),
        PaymentAmount: amountDue
    };

    return CartsDataActions.getCardPaymentAcceptPointAsync(
        { callerContext: ctx, bypassCache: 'get' },
        checkoutState.checkoutCart.cart.Id,
        cardPaymentAcceptSettings,
        []
    )
        // eslint-disable-next-line promise/prefer-await-to-then -- Existing code.
        .then(cardPaymentAcceptPoint => {
            if (!cardPaymentAcceptPoint.AcceptPageUrl && !cardPaymentAcceptPoint.AcceptPageContent) {
                throw new Error(
                    '[getCardPaymentAcceptPointAction]Payment Accept Page has neither return AcceptPageUrl nor AcceptPageContent in updatePaymentAcceptPageData'
                );
            }
            return cardPaymentAcceptPoint;
        })
        // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- Existing code.
        .catch(error => {
            ctx.telemetry.exception(error as Error);
            ctx.telemetry.debug('Unable to get Card Payment Accept Point');
            throw error;
        });
}

/**
 * Get card payment accept point.
 */
export const getCardPaymentAcceptPointActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce/checkout-express/get-card-payment-accept-point',
    action: getCardPaymentAcceptPointAction as IAction<CardPaymentAcceptPoint>,
    input: createCheckoutPaymentInstrumentInput
});

export default getCardPaymentAcceptPointActionDataAction;
